import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Check, Copy, Code, ExternalLink, X } from 'lucide-react';

export const EmbedCodeGenerator = ({ onClose }) => {
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('800');
  const [copied, setCopied] = useState(null);
  
  // Always use current domain for embed URLs (works with custom domains)
  const APP_URL = window.location.origin;
  const embedUrl = `${APP_URL}/embed`;
  
  // Standard iframe embed code
  const iframeCode = `<iframe
  src="${embedUrl}"
  width="${width}"
  height="${height}px"
  frameborder="0"
  allow="clipboard-write"
  style="border: 1px solid #27272a; border-radius: 8px;"
></iframe>`;

  // Shopify-specific Liquid code
  const shopifyCode = `{% comment %}
  Battery Pack Builder Embed - Add this to your Shopify theme
  Place in: Sections > Custom Liquid or Pages > Edit HTML
{% endcomment %}

<div class="battery-builder-container" style="width: 100%; max-width: 1400px; margin: 0 auto;">
  <iframe
    src="${embedUrl}"
    width="${width}"
    height="${height}px"
    frameborder="0"
    allow="clipboard-write"
    style="border: 1px solid #27272a; border-radius: 8px; width: 100%;"
    loading="lazy"
    title="Battery Pack Builder"
  ></iframe>
</div>

<style>
  @media (max-width: 768px) {
    .battery-builder-container iframe {
      height: 600px;
    }
  }
</style>`;

  // JavaScript snippet for dynamic embedding
  const jsSnippetCode = `<!-- Battery Pack Builder Widget -->
<div id="battery-pack-builder"></div>
<script>
(function() {
  var container = document.getElementById('battery-pack-builder');
  var iframe = document.createElement('iframe');
  iframe.src = '${embedUrl}';
  iframe.width = '${width}';
  iframe.height = '${height}';
  iframe.frameBorder = '0';
  iframe.allow = 'clipboard-write';
  iframe.style.cssText = 'border: 1px solid #27272a; border-radius: 8px; max-width: 100%;';
  iframe.loading = 'lazy';
  iframe.title = 'Battery Pack Builder';
  container.appendChild(iframe);
  
  // Listen for messages from the builder
  window.addEventListener('message', function(event) {
    if (event.data && event.data.type === 'BATTERY_BUILDER_RESULTS') {
      console.log('Battery Builder Results:', event.data.data);
      var customEvent = new CustomEvent('batteryConfigUpdated', { 
        detail: event.data.data 
      });
      document.dispatchEvent(customEvent);
    }
  });
})();
</script>`;

  const copyToClipboard = async (code, type) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const CodeBlock = ({ code, type, title, description }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-zinc-200">{title}</h4>
          <p className="text-xs text-zinc-500">{description}</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => copyToClipboard(code, type)}
          className="gap-2"
          data-testid={`copy-${type}-btn`}
        >
          {copied === type ? (
            <>
              <Check className="w-3 h-3 text-green-400" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              Copy
            </>
          )}
        </Button>
      </div>
      <pre className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 text-xs font-mono text-zinc-300 overflow-x-auto max-h-48 overflow-y-auto">
        {code}
      </pre>
    </div>
  );

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" 
      onClick={onClose}
      data-testid="embed-modal"
    >
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Code className="w-5 h-5 text-cyan-400" />
            <h3 className="font-heading text-lg font-bold text-zinc-100">Embed on Your Website</h3>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="hover:bg-zinc-800"
            data-testid="close-embed-modal"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Dimension Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Width</Label>
              <Input
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder="100% or 800"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Height (px)</Label>
              <Input
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="800"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
          </div>
          
          {/* Preview Link */}
          <div className="bg-zinc-800/50 rounded-lg p-3 flex items-center justify-between">
            <div>
              <span className="text-xs text-zinc-500">Embed URL:</span>
              <p className="text-sm text-cyan-400 font-mono">{embedUrl}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(embedUrl, '_blank')}
              className="gap-2"
              data-testid="preview-embed-btn"
            >
              <ExternalLink className="w-3 h-3" />
              Preview
            </Button>
          </div>
          
          {/* Shopify Code */}
          <CodeBlock
            code={shopifyCode}
            type="shopify"
            title="Shopify Embed Code"
            description="Copy this into your Shopify theme's Custom Liquid section or page HTML"
          />
          
          {/* Standard iframe */}
          <CodeBlock
            code={iframeCode}
            type="iframe"
            title="Standard iframe"
            description="Works on any website - just paste into your HTML"
          />
          
          {/* JavaScript Snippet */}
          <CodeBlock
            code={jsSnippetCode}
            type="js"
            title="JavaScript Snippet"
            description="Advanced embed with event callbacks for integration"
          />
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
          <p className="text-xs text-zinc-500 text-center">
            The embedded builder communicates with your page via postMessage events.
            Listen for <code className="text-cyan-400">batteryConfigUpdated</code> events to capture user configurations.
          </p>
        </div>
      </div>
    </div>
  );
};
